import { Barycentric2d, Barycentric3d } from "@base/math/Barycentric";
import { ConvexHull2, IOBB2, OBB2 } from "@base/math/ConvexHull2";
import { IMat4, Mat4 } from "@base/math/Mat4";
import { MathUtil } from "@base/math/MathUtil";
import { Plane } from "@base/math/Plane";
import { IVec2, Vec2 } from "@base/math/Vec2";
import { IVec3, Vec3 } from "@base/math/Vec3";
import { eqc } from "@base/util/Comma";
import { ImmHelper } from "@base/util/Immutable";
import { mapCount, PartialRecord } from "@base/util/TypeUtil";
import { RopeHelper } from "@biz/articles/RopeHelper";
import { Product } from "@biz/definitions/Product";
import { SSTForm } from "@biz/definitions/SSTForm";
import { EdgeName, IPatchData, IPoleData, IRopeArticleWithAuto, ISunSailData, MaterialType, PoleId, PoleIndex, PolePositionType, Ringschrauben, SunSailSides, SunSailType } from "@biz/definitions/SunSailDefinitions";
import { getEdgeName, getEdgeNameFromSunSailSide, getEdgePoleIds, getSideName } from "@biz/definitions/SunSailDefinitions.util";
import { PoleHelper } from "@biz/items/sunSail/logic/PoleHelper";
import { PoleLogic } from "@biz/items/sunSail/logic/PoleLogic";
import { Poles } from "@biz/items/sunSail/logic/Poles";
import { SunSailLogic } from "@biz/items/sunSail/logic/SunSailLogic";
import { TriangleHelper } from "@biz/items/sunSail/logic/TriangleHelper";
import { Sail2dPoly } from "@biz/items/sunSail/Sail2dPoly";
import { Sail2dReducers } from "@biz/items/sunSail/Sail2dReducers";
import { createInitialPole } from "@biz/items/sunSail/SunSailInitial";
import { getSail2d3dReducers } from "@biz/items/sunSail/SunSailReducers.2d3d";
import { getChecked } from "@biz/logic/getChecked";
import { TriangulationHelper } from "@biz/logic/TriangulationHelper";
import { ISail2dForm, ISail2dSandy } from "@biz/models/Sail2dDefinitions";
import { ensurePatches } from "@biz/reducers/EdgeReducer";
import { ISCPole, ISCSunSailData } from "@biz/sc/sunSail/ISCSunSailData";

export class SandyLogic {
  static checkPositionMode(sunSail: ISunSailData) {
    if (sunSail.positionMode === "net") {
      throw new Error("PositionMode 'net' not supported so far...!");
      /*if (!SST.is(sunSail.sunSailType, SST.Sandy))
        throw new Error("'net' just supported for sandy so far...!")
      if (sunSail.sourceConfigurator !== "2d")
        throw new Error("'net' just supported for sandy and sourceConfigurator 2d!")*/
    }
  }

  static calcCenterPolePositions(centerPoles: ISCPole[] | undefined, points: IVec2[]): (IVec2 | undefined)[] {
    const centerPolePositions: (IVec2 | undefined)[] = [];
    if (centerPoles?.length) {
      for (let i = 0; i < centerPoles.length; i++) {
        let pos: IVec2 | undefined;
        const cpt = centerPoles[i].centerPoleTriangulation;
        if (cpt) {
          pos = TriangulationHelper.calcSandyCenterTriangulationPoint(cpt, points);
        }
        centerPolePositions.push(pos);
      }
    }
    return centerPolePositions;
  }

  static getCenterRingSchraubenSetForMaterial(poleMaterial: MaterialType): string {
    const group = SunSailLogic.materialGroupFromMaterial(poleMaterial, undefined);
    switch (group) {
      case "alu": return Ringschrauben.M8x100_Double.articleNumber;
      case "iron48": return Ringschrauben.M8x60_Double.articleNumber;
    }
    throw new Error("getCenterRingSchraubenSetForMaterial not supported for material: " + poleMaterial);
  }

  static isValidPatchEdge(edgeName: EdgeName, poleCount: number): boolean {
    const poleIndices = getEdgePoleIds(edgeName);
    const p0 = Poles.fromPoleId(poleIndices[0]);
    const p1 = Poles.fromPoleId(poleIndices[1]);
    return (p0.polePositionType === "corner" && p1.polePositionType === "corner" && (p0.poleIndex as number) < poleCount && (p1.poleIndex as number) < poleCount);
  }

  static getPatches(sunSail: ISunSailData | ISCSunSailData | ISail2dForm): IPatchData[] {
    if (sunSail.patches && !Array.isArray(sunSail.patches))
      return [];
    return sunSail.patches || [];
  }

  static getPatch(sunSail: ISunSailData | ISCSunSailData | ISail2dForm, patchIndex: number): IPatchData {
    return SandyLogic.getPatches(sunSail)[patchIndex];
  }

  static calcSandyInputSidesAndDiagonals(poleCount: number): { sides: SunSailSides[]; diagonals: SunSailSides[] } {
    const sides: SunSailSides[] = [];
    for (let i = 0; i < poleCount; i++) {
      sides.push(getSideName(i, (i + 1) % poleCount))
    }

    const diagonals: SunSailSides[] = [];
    for (let i = 2; i < poleCount - 1; i++) {
      diagonals.push(getSideName(0, i));
    }
    return { sides, diagonals }
  }

  static calcCenterPoleBoltMounts(sunSail: ISunSailData, poleId: PoleId): { matrix: IMat4, center: IVec3, left: IVec3, right: IVec3 } {
    const topMatrix = SunSailLogic.calcMatrixForPos(sunSail, poleId, SandyLogic.calcSandyBoltOffset(), "top");
    const groundMatrix = SunSailLogic.calcPoleGroundMatrixFromValues(SunSailLogic.getPolePos(sunSail, poleId), Math.PI + SandyLogic.getCoilUpPoleRotation(sunSail, poleId));
    const matrix = Mat4.muls(groundMatrix, topMatrix);
    const offset = PoleLogic.getPoleDiameterMeter(SunSailLogic.getPoleMaterial(sunSail, poleId)) * 0.5 + 0.033;
    return { matrix, center: Vec3.applyMatrix4(Vec3.ZERO, matrix), left: Vec3.applyMatrix4(Vec3.from(0, 0.005, offset), matrix), right: Vec3.applyMatrix4(Vec3.from(0, 0.005, -offset), matrix) }
  }

  static calcSandyBoltOffset(): number {
    return 0.04;
  }

  static calcSandyRopeMount(sunSail: ISunSailData, poleId: PoleId, options?: { relative?: boolean }): { m: IMat4, rope: IVec3 } {
    const groundMatrix = SunSailLogic.calcPoleGroundMatrix(sunSail, poleId);
    const poleLength = SunSailLogic.getPoleLength(sunSail, poleId);
    const poleSegments = SunSailLogic.calcPoleSegments(sunSail, poleId);
    const top = PoleHelper.calcMatrixForLengthPosFromSegments(poleSegments, SandyLogic.calcSandyBoltOffset(), PoleHelper.getTopOptions(poleLength))
    const m = options?.relative ? top : Mat4.mul(groundMatrix, top)


    const offset = 0.03;
    const rope = Vec3.applyMatrix4(Vec3.from(0, 0, -PoleLogic.getPoleDiameterMeter(SunSailLogic.getPoleMaterial(sunSail, poleId)) * 0.5 - offset), m);
    return {
      m,
      rope
    }
  }

  static calcTopMatrix(sunSail: ISunSailData, poleId: PoleId): IMat4 {
    const m1 = Mat4.muls(SunSailLogic.calcPoleGroundMatrix(sunSail, poleId), SunSailLogic.calcMatrixForPos(sunSail, poleId, 0, "top"));
    return m1;
  }

  static calcSandyRopeClampMatrix(sunSail: ISunSailData, poleId: PoleId): IMat4 {
    return SunSailLogic.calcMatrixForPos(sunSail, poleId, 0.1, "niveau");
  }

  static getOperatingRopePolePosition(sunSail: ISunSailData, centerPoleId: PoleId): IVec3 {
    const coilUpPoleId = SandyLogic.getOperatingRopePoleForCenterPoleId(sunSail, centerPoleId);
    return SandyLogic.calcSandyRopeMount(sunSail, coilUpPoleId).rope
  }

  static getCoilUpPoleRotation(sunSail: ISunSailData, poleId: PoleId): number {
    const polePosition = SunSailLogic.getPolePos(sunSail, poleId);
    const operatingPolePosition = SandyLogic.getOperatingRopePolePosition(sunSail, poleId);
    const operatingRopeDir = Vec3.to(polePosition, operatingPolePosition)
    const angle = Math.atan2(operatingRopeDir.z, -operatingRopeDir.x);
    return angle;
  }

  static getOperatingRopePoleForCenterPoleId(sunSail: ISunSailData, poleId: PoleId): PoleId {
    const pid = Poles.fromPoleId(poleId);
    if (pid.polePositionType !== "center")
      throw new Error("Not a center pole - getCoilUpPoleForCenterPoleId - " + poleId);
    const centerPole = sunSail.centerPoles?.[pid.poleIndex];
    if (!centerPole)
      throw new Error("No center pole found - getCoilUpPoleForCenterPoleId - " + poleId)
    return SandyLogic.getOperatingPoleForCenterPole(centerPole)
  }

  static getOperatingPoleForCenterPole(pole: IPoleData | ISCPole): PoleId {
    return pole.sandyOperatingRopePoleId || PoleIndex.A;
  }

  static isRopeOperatingPole(sunSail: ISunSailData | ISCSunSailData | ISail2dForm, poleId: PoleId): "ropeOnly" | "operating" | false {
    const patches = SandyLogic.getPatches(sunSail);

    if (sunSail.centerPoles) {
      for (const p of sunSail.centerPoles) {
        if (SandyLogic.getOperatingPoleForCenterPole(p) === poleId)
          return "operating";
      }
    }

    let ret: "ropeOnly" | "operating" | false = false;
    for (const patch of patches) {
      if (patch.articleId) {
        if (patch.poleIds[0] === poleId)
          return "operating";
        else if (patch.poleIds[1] === poleId)
          ret = "ropeOnly";
      }
    }

    return ret;
  }

  static calcBottomSandyPositions(sunSail: ISunSailData): { sailPoints: IVec3[], cornerPoints: IVec3[], centerPoints: IVec3[] } {
  // according to Soliday, the sail positions for sandy system has to be calculated via the pole bottom positions and distIns. ?!?
    const sailPoints: IVec3[] = [];
    const cornerPoints: IVec3[] = [];

    if (sunSail.positionMode === "net") {
      throw new Error("calcBottomSandyPositions not supported for net position mode so far...!");
      for (let i = 0; i < sunSail.poles.length; i++) {
        sailPoints[i] = sunSail.poles[i].position;
      }
      for (let i = 0; i < sunSail.poles.length; i++) {
        cornerPoints[i] = SandyLogic.netToMountPoint3d(sunSail, i);
      }
    } else {
      for (let i = 0; i < sunSail.poles.length; i++) {
        cornerPoints[i] = sunSail.poles[i].position;
      }
      for (let i = 0; i < sunSail.poles.length; i++) {
        const half = sunSail.poles[i].halfVec ?? TriangleHelper.calcHalfVector(cornerPoints[i], cornerPoints[(i - 1 + cornerPoints.length) % cornerPoints.length], cornerPoints[(i + 1) % cornerPoints.length], { correctDir: "left" });
        const distIn = 0.3;
        sailPoints[i] = Vec3.addScaled(cornerPoints[i], half, distIn);
      }
    }

    const centerPoints: IVec3[] = mapCount(sunSail.centerPoles?.length || 0, (index) => SunSailLogic.getPolePos(sunSail, Poles.toPoleId(index, "center")));
    return { sailPoints, centerPoints, cornerPoints };
  }

  static _calcUnwrappedPositionByTriangle(cp: IVec3, triSail: IVec3[], triUnwrapped: IVec2[]): IVec2 | undefined {
    const decimalPlaces = 3;
    if (eqc(triSail[0].x, triSail[1].x, decimalPlaces) && eqc(triSail[0].x, triSail[2].x, decimalPlaces)) {
      return undefined;
    }
    if (eqc(triSail[0].z, triSail[1].z, decimalPlaces) && eqc(triSail[0].z, triSail[2].z, decimalPlaces)) {
      return undefined;
    }

    const plane = Plane.fromPoints(triSail[0], triSail[1], triSail[2]);
    if (Plane.isNan(plane)) {
      return undefined;
    }
    const y = Plane.calcY(plane, cp.x, cp.z)
    const planePoint = Vec3.from(cp.x, y, cp.z);
    const barycentric = Barycentric3d.fromPoint(planePoint, triSail[0], triSail[1], triSail[2])
    // const t = Barycentric2d.toPoint(barycentric, triSail[0], triSail[1], triSail[2]);
    // console.log("Test", planePoint, t);
    const p = Barycentric2d.toPoint(barycentric, triUnwrapped[0], triUnwrapped[1], triUnwrapped[2]);
    if (Number.isNaN(p.x))
      return undefined;
    return p;
  }

  static _calcUnwrappedPositionAroundPoint(cp: IVec3, sailPoints: IVec3[], unwrappedPoints: IVec2[], aroundPoint: number): IVec2 | undefined {
    const firstPoint = (aroundPoint - 1 + sailPoints.length) % sailPoints.length;
    let lastPoint = (aroundPoint + 1) % sailPoints.length;
    let p: IVec2 | undefined;
    do {
      const indices = [firstPoint, aroundPoint, lastPoint];
      p = SandyLogic._calcUnwrappedPositionByTriangle(cp, indices.map((index) => sailPoints[index]), indices.map((index) => unwrappedPoints[index]));
      if (p) {
        return p;
      }
      lastPoint = (lastPoint + 1) % sailPoints.length
    } while (lastPoint !== firstPoint)
    return undefined;
  }

  static _calcUnwrappedPosition(cp: IVec3, sailPositions: IVec3[], unwrapped: IVec2[]): IVec2 | undefined {
    for (let iSailPosition = 1; iSailPosition < sailPositions.length - 1; iSailPosition++) {
      const indices = [0, iSailPosition, iSailPosition + 1]
      const triSail2d = indices.map((index) => Vec2.fromXZ(sailPositions[index]));
      if (Sail2dPoly.isPointInPoly(triSail2d, Vec2.fromXZ(cp))) {
        return SandyLogic._calcUnwrappedPositionByTriangle(cp, indices.map((index) => sailPositions[index]), indices.map((index) => unwrapped[index]));
      }
    }
    return SandyLogic._calcUnwrappedPositionAroundPoint(cp, sailPositions, unwrapped, 0);
  }

  static calcSandyClothArea(sunSail: ISunSailData): number {
    const { sailPoints } = SandyLogic.calcBottomSandyPositions(sunSail);
    let area = 0;
    for (let i = 2; i < sailPoints.length; i++) {
      const a = Vec3.len(sailPoints[0], sailPoints[i - 1]);
      const b = Vec3.len(sailPoints[i - 1], sailPoints[i]);
      const c = Vec3.len(sailPoints[i], sailPoints[0]);
      area += TriangleHelper.calcArea(a, b, c);
    }
    return area;
  }

  static calcSandySailUnwrappedPositions(sunSail: ISunSailData): { sailPoints: IVec2[]; cornerPoints: IVec2[], patchPositions: { from: IVec2, to: IVec2; center: IVec2 }[], centerPoints: (IVec2 | undefined)[]; errors?: number[] } {
    const { sailPoints, centerPoints, cornerPoints } = SandyLogic.calcBottomSandyPositions(sunSail);
    const unwrappedSail = SandyLogic.#unwrap3dTo2d(sailPoints);

    const unwrappedCenterPoints: (IVec2 | undefined)[] = [];
    if (centerPoints.length) {
      for (let iCenterPole = 0; iCenterPole < centerPoints.length; iCenterPole++) {
        const cp = centerPoints[iCenterPole];
        const unwrappedPoint = SandyLogic._calcUnwrappedPosition(cp, sailPoints, unwrappedSail.points)
        unwrappedCenterPoints.push(unwrappedPoint);
      }
    }

    const unwrappedCornerPoints: IVec2[] = [];
    for (let i = 0; i < cornerPoints.length; i++) {
      const unwrappedPoint = SandyLogic._calcUnwrappedPositionAroundPoint(cornerPoints[i], sailPoints, unwrappedSail.points, i);
      unwrappedCornerPoints.push(unwrappedPoint ?? Vec2.NAN);
    }

    const patchPositions: { from: IVec2; to: IVec2; center: IVec2 }[] = [];
    if (sunSail.patches) {
      for (let i = 0; i < sunSail.patches.length; i++) {
        const patch = sunSail.patches[i];
        const from3d = cornerPoints[patch.poleIds[0]];
        const to3d = cornerPoints[patch.poleIds[1]];
        const center = SandyLogic._calcUnwrappedPosition(Vec3.center([from3d, to3d]), sailPoints, unwrappedSail.points) ?? Vec2.NAN;
        patchPositions.push({ from: unwrappedCornerPoints[patch.poleIds[0]], to: unwrappedCornerPoints[patch.poleIds[1]], center })
      }
    }

    return { sailPoints: unwrappedSail.points, centerPoints: unwrappedCenterPoints, patchPositions, errors: unwrappedSail.errors, cornerPoints: unwrappedCornerPoints };
  }

  static calcSandySailOBB(sunSail: ISunSailData): IOBB2 | undefined {
    const { sailPoints: points, errors } = SandyLogic.calcSandySailUnwrappedPositions(sunSail);
    if (errors?.length)
      return undefined;

    const hull = ConvexHull2.calc(points);
    // arrows.push(...calcArrows(hull.map((v) => Vec3.from(v.x, 0, v.y)), { type: "loop" }));
    const obb = OBB2.calcOBB(hull);
    return obb;
  }

  static calcSandyObbArea(sunSail: ISunSailData | ISCSunSailData): { area: number, obb: IOBB2 } | undefined {
    if (Product.isProductSC(sunSail))
      throw new Error("Sandy shop area calculation not supported so far")
    const obb = SandyLogic.calcSandySailOBB(sunSail);
    if (!obb)
      return undefined;
    const area = OBB2.calcArea(obb);
    return { area, obb };
  }

  static calcSandySideAndDiagonalLengths(positions: IVec3[]): PartialRecord<EdgeName, number> {
    const origLengths: PartialRecord<EdgeName, number> = {};
    for (let i = 0; i < positions.length; i++) {
      const nextIndex = (i + 1) % positions.length;
      origLengths[getEdgeName(i, nextIndex)] = Vec3.len(positions[i], positions[nextIndex]);
    }
    for (let i = 2; i < positions.length - 1; i++) {
      origLengths[getEdgeName(0, i)] = Vec3.len(positions[0], positions[i]);
    }
    return origLengths;
  }

  static #unwrap3dTo2d(positions: IVec3[]): { points: IVec2[], errors?: number[] } {
    // unwrap 3d positions to 2d positions by keeping side and diagonal lengths intact

    const origLengths = SandyLogic.calcSandySideAndDiagonalLengths(positions);
    return SandyLogic.#dimensionsToPoints(Vec2.fromXZ(positions[0]), Vec2.dir(Vec2.fromXZ(positions[0]), Vec2.fromXZ(positions[1])), positions.length, origLengths)
  }

  static calcPositionModePoints(sunSail: ISail2dSandy): {
    sail: IVec2[];
    mount: IVec2[];
    halfVecs: IVec2[];
    centerPoints: (IVec2 | undefined)[];
    errors?: number[];
  } {
    const ret = sunSail.sunSailType === SunSailType.SandyRect ? SandyLogic.#rectToPoints(sunSail) : SandyLogic.#dimensionsToPoints(Vec2.ZERO, Vec2.from(1, 0), sunSail.poleCount, sunSail.dimensions);
    const halfVecs: IVec2[] = [];
    for (let i = 0; i < sunSail.poles.length; i++) {
      const { last, next } = SandyLogic.calcLastAndNextMainPoles(sunSail, i);
      if (sunSail.poles[i].additionalPole) {
        halfVecs[i] = Vec2.normalVector(Vec2.dir(ret.points[i], ret.points[last]), true);
      } else {
        halfVecs[i] = TriangleHelper.calcHalfVector2(ret.points[i], ret.points[last], ret.points[next], { correctDir: "right" });
      }
    }

    const distIn = 0.3;
    const sail: IVec2[] = [...ret.points];
    const mount: IVec2[] = [...ret.points];
    if (sunSail.positionMode === "net") {
      for (let i = 0; i < sail.length; i++) {
        mount[i] = Vec2.addScaled(sail[i], halfVecs[i], -distIn);
      }
    } else {
      for (let i = 0; i < sunSail.poles.length; i++) {
        if (sunSail.poles[i].additionalPole) {
          mount[i] = Vec2.addScaled(mount[i], halfVecs[i], -(1 - Math.sqrt(0.5)) * distIn);
        }
      }
      for (let i = 0; i < mount.length; i++) {
        sail[i] = Vec2.addScaled(mount[i], halfVecs[i], distIn);
      }
    }
    const centerPoints = SandyLogic.calcCenterPolePositions(sunSail.centerPoles, mount); // calculate center points before modifiying due to calculation type
    return {
      errors: ret.errors,
      sail,
      mount,
      halfVecs,
      centerPoints
    }
  }

  static calcGroundPoints2d(sunSail: ISail2dSandy): {
    mount: IVec2[];
    centerPoints: (IVec2 | undefined)[];
    errors?: number[];
  } {
    const ret = SandyLogic.calcPositionModePoints(sunSail);
    return ret;
  }

  static netToMountPoint3d(ssd: ISunSailData, poleId: PoleId): IVec3 {
    const pole = ssd.poles[poleId];
    const half = pole.halfVec ?? TriangleHelper.calcHalfVector(pole.position, ssd.poles[(poleId - 1 + ssd.poles.length) % ssd.poles.length].position, ssd.poles[(poleId + 1) % ssd.poles.length].position, { correctDir: "right" });
    const distIn = 0.3;
    return Vec3.addScaled(pole.position, half, -distIn * 0);
  }

  static #rectCornerPoints(sunSail: ISail2dSandy): IVec2[] {
    const cornerPoints: IVec2[] = [];
    cornerPoints[0] = Vec2.from(0, 0);
    cornerPoints[1] = Vec2.from(sunSail.dimensions.ab || 0, 0);
    cornerPoints[2] = Vec2.from(sunSail.dimensions.ab || 0, sunSail.dimensions.bc || 0);
    cornerPoints[3] = Vec2.from(0, sunSail.dimensions.bc || 0);
    return cornerPoints;
  }

  static #rectToPoints(sunSail: ISail2dSandy): { points: IVec2[], errors?: PoleId[] } {
    const points: IVec2[] = [];
    const errors: PoleId[] = [];

    const cornerPoints = SandyLogic.#rectCornerPoints(sunSail);
    {
      let cornerPointIndex = 0;
      for (let i = 0; i < sunSail.poles.length; i++) {
        if (!sunSail.poles[i].additionalPole) {
          points[i] = (cornerPoints[cornerPointIndex++]);
        }
      }
    }

    let cornerPointIndex = 0;
    let distSum = 0;
    for (let i = 0; i < sunSail.poles.length; i++) {
      const ap = sunSail.poles[i].additionalPole;
      if (ap) {
        distSum += ap.distance;
        const p1 = cornerPoints[cornerPointIndex - 1];
        const p2 = cornerPoints[(cornerPointIndex) % cornerPoints.length];
        const np = Vec2.addScaled(p1, Vec2.dir(p1, p2), distSum);
        points[i] = np;
        if (!np)
          errors.push(i);
        // let np: IVec2;
        // if (sunSail.positionMode === "net") {
        //   // const { last, next } = SandyLogic.calcLastAndNextMainPoles(ssd, i);
        //   const p1 = cornerPoints[cornerPointIndex - 1];
        //   const p2 = cornerPoints[(cornerPointIndex) % cornerPoints.length];
        //   np = Vec2.addScaled(p1, Vec2.dir(p1, p2), distSum);
        // } else {
        //   const p1 = cornerPoints[cornerPointIndex - 1];
        //   const p2 = cornerPoints[(cornerPointIndex) % cornerPoints.length];
        //   const dir = Vec2.dir(p1, p2);
        //   np = Vec2.addScaled(p1, dir, distSum);
        //   const n = Vec2.normalVector(dir, false);
        //   const distIn = 0.3;
        //   const borderDistIn = distIn * (1 - Math.sqrt(2)); // for rectangle only
        //   np = Vec2.addScaled(np, n, borderDistIn);
        // }
      } else {
        cornerPointIndex++;
        distSum = 0;
      }
    }
    return { points, errors };
  }

  static #dimensionsToPoints(initialPos: IVec2, initialDir: IVec2, pointCount: number, dimensions: PartialRecord<EdgeName, number>): { points: IVec2[], errors?: PoleId[] } {
    // unwrap 3d positions to 2d positions by keeping side and diagonal lengths intact
    const points: IVec2[] = [];
    const errors: PoleId[] = [];
    points[0] = initialPos;
    points[1] = Vec2.addScaled(points[0], initialDir, dimensions[getEdgeName(0, 1)] || 0);

    for (let i = 2; i < pointCount; i++) {
      const lenAC = dimensions[getEdgeName(0, i)] || 0;
      const lenBC = dimensions[getEdgeName(i - 1, i)] || 0;
      const np = SandyLogic.#calcNextTrianglePoint(points[0], points[i - 1], lenAC, lenBC);
      if (!np) {
        errors.push(i - 2);
      }
      if (np)
        points[i] = np;
    }

    return {
      points,
      errors: errors.length ? errors : undefined
    }
  }

  static #calcNextTrianglePoint(a: IVec2, b: IVec2, acLen: number, bcLen: number): IVec2 | undefined {
    const ab = Vec2.dirLen(a, b);
    const heightC = TriangleHelper.calcHeightC(acLen, bcLen, ab.len);
    if (heightC === undefined) {
      return undefined;
    }

    if (acLen >= bcLen) {
      const inner = acLen ** 2 - heightC ** 2;
      if (inner < 0) {
        return undefined;
      }
      const c1 = Math.sqrt(inner);
      return Vec2.addScaled(Vec2.addScaled(a, ab.dir, c1), Vec2.normalVector(ab.dir, false), heightC);
    } else {
      const inner = bcLen ** 2 - heightC ** 2;
      if (inner < 0) {
        return undefined;
      }
      const c1 = Math.sqrt(inner);
      return Vec2.addScaled(Vec2.addScaled(b, ab.dir, -c1), Vec2.normalVector(ab.dir, false), heightC);
    }
  }

  static addSandyPole(old: ISunSailData, poleIdOrPositionType: PoleId | PolePositionType): { sunSailData: ISunSailData; selectedPoleId: PoleId } {
    let polePositionType: PolePositionType;
    let poleIndex: PoleIndex;
    if (typeof poleIdOrPositionType === "string") {
      polePositionType = poleIdOrPositionType;
      const count = Poles.getCount(old, poleIdOrPositionType);
      poleIndex = count - 1;
    } else {
      const pid = Poles.fromPoleId(poleIdOrPositionType)
      polePositionType = pid.polePositionType;
      poleIndex = pid.poleIndex;
    }

    const poleId = Poles.toPoleId(poleIndex, polePositionType);

    const poleProperty = Poles.getPolePositionProperty(polePositionType);

    if (polePositionType === "corner") {
      const pole = Poles.getPole(old, poleId);
      const poleToAdd = { ...pole, position: SandyLogic.calcNewCornerPolePosition(old, poleId), }

      const selectedPoleIndex = poleIndex + 1;
      old = SandyLogic.correctCornerPoleCountChange(old, selectedPoleIndex, 1);
      return {
        sunSailData: { ...old, [poleProperty]: ImmHelper.insertArrayItem(old[poleProperty] || [], selectedPoleIndex, poleToAdd) },
        selectedPoleId: Poles.toPoleId(selectedPoleIndex, polePositionType)
      }
    } else {
      const pole = Poles.isValid(old, poleId) ? Poles.getPole(old, poleId) : createInitialPole(old, Poles.getCount(old), poleId, Vec3.ZERO, old.featureLevel);
      const poleToAdd = { ...pole, position: SandyLogic.calcNewCenterPolePosition(old, poleId), }

      const selectedPoleIndex = Math.max(poleIndex + 1, Poles.getCount(old, polePositionType));
      return {
        sunSailData: { ...old, [poleProperty]: ImmHelper.insertArrayItem(old[poleProperty] || [], selectedPoleIndex, poleToAdd) },
        selectedPoleId: Poles.toPoleId(selectedPoleIndex, polePositionType)
      }
    }
  }

  static correctCornerPoleCountChange<T extends (ISunSailData | ISail2dForm)>(old: T, poleIndex: number, change: number): T {
    const reducers = getSail2d3dReducers<T>()
    {
      const length = old.centerPoles?.length ?? 0;
      for (let i = 0; i < length; i++) {
        const sandyOperatingRopePoleId = old.centerPoles![i].sandyOperatingRopePoleId;
        if (sandyOperatingRopePoleId && sandyOperatingRopePoleId >= poleIndex) {
          old = reducers.setSandyOperatingRopePoleId(old, Poles.toPoleId(i, "center"), sandyOperatingRopePoleId + change);
        }
        if (Product.isProduct2d(old)) {
          let old2d = old;
          const cp = old.centerPoles![i];
          if (cp.centerPoleTriangulation) {
            const { a, b } = SandyLogic.#correctPoleIds(poleIndex, change, Poles.getCount(old, "corner"), cp.centerPoleTriangulation[0].poleId, cp.centerPoleTriangulation[1].poleId);

            old2d = Sail2dReducers.setSandyCenterTriangulationPoleId(old2d, Poles.toPoleId(i, "center"), 0, a)
            old2d = Sail2dReducers.setSandyCenterTriangulationPoleId(old2d, Poles.toPoleId(i, "center"), 1, b)
          }
          old = old2d;
        }
      }
    }
    {
      const length = old.patches?.length ?? 0;
      for (let i = 0; i < length; i++) {
        const patch = old.patches![i];
        const { a, b } = SandyLogic.#correctPoleIds(poleIndex, change, Poles.getCount(old, "corner"), patch.poleIds[0], patch.poleIds[1]);
        old = reducers.setSandyPatchPoleId(old, i, 0, a);
        old = reducers.setSandyPatchPoleId(old, i, 1, b);
      }
    }
    return old;
  }

  static #correctPoleIds(poleIndex: number, change: number, poleCount: number, a: PoleId, b: PoleId): { a: PoleId; b: PoleId; } {
    if (a >= poleIndex) {
      a = Math.max(0, a + change);
    }
    if (b >= poleIndex) {
      b = Math.max(0, b + change);
    }
    if (a === b) {
      b = (a + 1) % (poleCount);
    }
    if (a < 0 || b < 0) {
      throw new Error("Invalid poleIndex!");
    }
    return { a, b };
  }


  static removeSandyPole(old: ISunSailData, poleId: PoleId): {
    sunSailData: ISunSailData,
    selectedPoleId: PoleId | undefined
  } {
    const pid = Poles.fromPoleId(poleId)

    if (pid.polePositionType === "corner" && Poles.getCount(old) <= 3)
      return { sunSailData: old, selectedPoleId: undefined };

    const poleProperty = Poles.getPolePositionProperty(pid.polePositionType);
    old = { ...old, [poleProperty]: ImmHelper.removeArrayItem(old[poleProperty] || [], pid.poleIndex), }
    if (pid.polePositionType === "corner")
      old = SandyLogic.correctCornerPoleCountChange(old, pid.poleIndex, -1);
    return {
      sunSailData: old,
      selectedPoleId: Poles.getCount(old, pid.polePositionType) === 0 ? undefined : Poles.calcPoleIdWithOffset(poleId, -1, old)
    }
  }

  static calcNewCornerPolePosition(old: ISunSailData, poleId: PoleId): IVec3 {
    const a = SunSailLogic.getPolePos(old, poleId);
    const b = SunSailLogic.getPolePos(old, poleId, { offset: 1 });
    const dl = Vec3.dirLen(a, b);
    const n = Vec2.normalVector(Vec2.fromXZ(dl.dir), true);
    return Vec3.addScaled(Vec3.mid(a, b), Vec3.fromXY(n), dl.len * 0.1)
  }

  static calcNewCenterPolePosition(old: ISunSailData, poleId: PoleId): IVec3 {
    if (!Poles.isValid(old, poleId)) {
      return Vec3.center(mapCount(old.poles.length, (index) => SunSailLogic.getPolePos(old, index)))
    }
    return Vec3.mid(SunSailLogic.getPolePos(old, poleId), SunSailLogic.getPolePos(old, 0));
  }

  static addPatch(old: ISail2dForm, patch: IPatchData | undefined): ISail2dForm;
  static addPatch(old: ISunSailData, patch: IPatchData | undefined): ISunSailData;
  static addPatch(old: ISunSailData | ISail2dForm, patch: IPatchData | undefined): ISunSailData | ISail2dForm {
    old = ensurePatches(old);
    if (!patch)
      patch = SandyLogic.createNewPatch(Poles.getCount(old), old.patches);
    if (!patch)
      return old;
    return ImmHelper.setProperty(old, "patches", ImmHelper.pushArrayItem(old.patches!, patch));
  }

  static removePatch(old: ISunSailData, patchIndex: number): {
    sunSailData: ISunSailData,
    selectedPatchIndex: number | undefined
  } {
    if (!old.patches?.length)
      return { sunSailData: old, selectedPatchIndex: undefined }
    return {
      sunSailData: ImmHelper.setProperty(old, "patches", ImmHelper.removeArrayItem(old.patches!, patchIndex)),
      selectedPatchIndex: old.patches!.length <= 1 ? undefined : Math.max(patchIndex - 1, 0)
    }
  }

  static createNewPatch(poleCount: number, existingPatches?: IPatchData[]): IPatchData {
    const usedPoles = mapCount(poleCount, () => false);
    if (existingPatches) {
      for (const p of existingPatches) {
        usedPoles[p.poleIds[0]] = true;
        usedPoles[p.poleIds[1]] = true;
      }
    }
    let fromPoleId = 0;
    for (let i = 0; i < usedPoles.length; i++) {
      if (!usedPoles[i]) {
        fromPoleId = i;
        break;
      }
    }

    const toPoleId = (fromPoleId + Math.floor(poleCount / 2)) % poleCount;
    const newPatch: IPatchData = {
      articleId: "00E13000767",
      poleIds: [fromPoleId, toPoleId],
    }
    return newPatch;
  }

  static calcPatchOperatingRope(sunSail: ISunSailData | ISail2dForm, iPatchIndex: number): IRopeArticleWithAuto {
    const patch = SandyLogic.getPatch(sunSail, iPatchIndex);
    const length = patch.sandyOperatingRope?.length;
    const articleId = patch.sandyOperatingRope?.articleId || "00E13000023";
    if (length === undefined) {
      const ropeLength = SandyLogic.#calcPatchOperatingRopeAutoLength(sunSail, iPatchIndex);
      return { length: ropeLength, articleId, auto: true }
    }
    return { length, articleId, auto: false }
  }

  static #calcPatchOperatingRopeAutoLength(sunSail: ISunSailData | ISail2dForm, iPatchIndex: number): number {

    const patch = SandyLogic.getPatch(sunSail, iPatchIndex);
    const poleIndices = patch.poleIds;

    let dist = 0;
    if (Product.isProduct2d(sunSail)) {
      if (SSTForm.is(sunSail, SSTForm.Sandy)) {
        const { mount, errors } = SandyLogic.calcGroundPoints2d(sunSail)
        if (errors?.length)
          return 0;
        dist = Vec2.len(mount[poleIndices[0]], mount[poleIndices[1]])
      }
    } else {
      const mountPositions = SunSailLogic.calcMountPositions(sunSail, 0, undefined);
      dist = Vec3.len(mountPositions[poleIndices[0]], mountPositions[poleIndices[1]]);
    }

    const d2 = dist * 0.5;
    const ropeLength = Math.ceil(Math.sqrt(3 ** 2 + d2 ** 2) * 2 + 3); // https://plaspack.atlassian.net/browse/API-86
    return ropeLength;
  }

  static calcCenterPoleOperatingRope(sunSail: ISunSailData | ISail2dForm, centerPoleId: PoleId): IRopeArticleWithAuto | undefined {
    const centerPole = Poles.getPole(sunSail, centerPoleId)
    const operatingPoleId = SandyLogic.getOperatingPoleForCenterPole(centerPole);

    if (!Poles.isValid(sunSail, operatingPoleId)) {
      return undefined;
    }

    const length = centerPole.sandyOperatingRope?.length;
    const articleId = centerPole.sandyOperatingRope?.articleId || "00E13000023";
    if (length === undefined) {
      const ropeLength = SandyLogic.#calcCenterPoleOperationRopeAutoLength(sunSail, centerPoleId);
      return { length: ropeLength, articleId, auto: true }
    }
    return { length, articleId, auto: false }
  }

  static #calcCenterPoleOperationRopeAutoLength(sunSail: ISunSailData | ISail2dForm, centerPoleId: PoleId): number {
    if (Product.isProduct2d(sunSail)) {
      if (SSTForm.is(sunSail, SSTForm.Sandy)) {
        const { mount, centerPoints, errors } = SandyLogic.calcGroundPoints2d(sunSail)
        if (errors?.length)
          return 0;
        const centerPole = Poles.getPole(sunSail, centerPoleId)
        const operatingPoleId = SandyLogic.getOperatingPoleForCenterPole(centerPole);
        const rope1 = 3 * 2; // poleLength * 2

        const centerPolePos = centerPoints[centerPoleId];
        if (centerPolePos) {
          const rope2 = Vec2.len(centerPolePos, mount[operatingPoleId]) + 3
          const ropeLength = Math.ceil(rope1 + rope2);
          return ropeLength;
        }
      }
      return 0;
    } else {
      const centerPole = Poles.getPole(sunSail, centerPoleId)
      const operatingPoleId = SandyLogic.getOperatingPoleForCenterPole(centerPole);
      const rope1 = SunSailLogic.getPoleLength(sunSail, centerPoleId) * 2;
      const mp = SandyLogic.calcCenterPoleBoltMounts(sunSail, centerPoleId);
      const mountPositions = SunSailLogic.calcMountPositions(sunSail, 0, {});
      const rope2 = Vec3.len(mp.center, mountPositions[operatingPoleId]) + 3
      const ropeLength = Math.ceil(rope1 + rope2);
      return ropeLength;
    }
  }

  static getValidCenterOperatingRopes(sunSail: ISunSailData | ISail2dForm, centerPoleId: PoleId): string[] {
    if (!Poles.isValid(sunSail, centerPoleId)) {
      return [];
    }

    const ropes = RopeHelper.getRopeArticles(["6.5"]);//, "8"]);
    return ropes.map((r) => r.id)
  }


  static getValidPatchOperatingRopes(sunSail: ISunSailData | ISail2dForm, patchIndex: number): string[] {
    if (!SandyLogic.getPatch(sunSail, patchIndex))
      return [];

    const ropes = RopeHelper.getRopeArticles(["6.5"]);//, "8"]);
    return ropes.map((r) => r.id)
  }

  static getOperatingRopeKlampe(sunSail: ISunSailData | ISCSunSailData | ISail2dForm, poleId: PoleId): string | undefined {
    const validItems = SandyLogic.getPossibleOperatingRopeKlampen(sunSail, poleId);
    const klampe = Poles.getPole(sunSail, poleId).articleSlots?.operatingRopeKlampe;
    return getChecked({
      selection: klampe ?? SandyLogic.getPossibleOperatingRopeKlampen(sunSail, poleId)[0],
      validItems,
      emptySelection: ""
    });
  }

  static getPossibleOperatingRopeKlampen(sunSail: ISunSailData | ISCSunSailData | ISail2dForm, poleId: PoleId): string[] {
    if (SandyLogic.isRopeOperatingPole(sunSail, poleId) === "operating") {
      const materialGroup = SunSailLogic.getPoleMaterialGroup(sunSail, poleId);
      switch (materialGroup) {
        case "alu":
          return ["00E13001414"]
        case "iron48":
          return ["00E13000522"]

      }
    }
    return [];
  }

  static calcInitialSandyDimensions(sunSailType: SunSailType, poleCount: number): PartialRecord<EdgeName, number> {
    if (sunSailType === SunSailType.SandyRect) {
      return {
        "ab": 5,
        "bc": 3,
      }
    }

    const sideLength = 3;

    const sandy = SandyLogic.calcSandyInputSidesAndDiagonals(poleCount);
    const ret: PartialRecord<EdgeName, number> = {};
    for (const side of sandy.sides) {
      ret[getEdgeNameFromSunSailSide(side)] = sideLength;
    }

    for (let i = 0; i < sandy.diagonals.length; i++) {
      const side = sandy.diagonals[i];
      const l = SandyLogic.calcRegularPolyDiagonal(sideLength, i, poleCount);
      ret[getEdgeNameFromSunSailSide(side)] = l;
    }
    return ret;
  }

  static calcRegularPolyDiagonal(sideLength: number, i: number, cornerCount: number): number {
    const diagonalCount = cornerCount - 3;
    if (i >= diagonalCount / 2) {
      i = diagonalCount - i - 1;
    }

    // given a regular polygon with n sides and side length a, calculate the length of digonal with index i
    const r = sideLength / (2 * Math.sin(Math.PI / cornerCount));
    const len = MathUtil.round(2 * r * Math.sin((i + 2) * Math.PI / cornerCount), 3);
    return len;
  }

  static getCenterDistanceTriangulationPoint(sunSail: ISail2dForm, centerPoleId: PoleId, triangulationIndex: number): PoleId {
    const centerPole = Poles.fromPoleId(centerPoleId);
    if (centerPole.polePositionType !== "center") {
      throw new Error("Not a center pole - getCenterDistanceTriangulationPoint - " + centerPoleId);
    }
    return Poles.getPole(sunSail, centerPoleId)?.centerPoleTriangulation?.[triangulationIndex]?.poleId || PoleIndex.A;
  }

  // static getAdditionalPoint(sunSail: ISail2dForm, additionalPointIndex: number): ISandyAdditionalPoint | undefined {
  //   if (!SSTForm.is(sunSail, SSTForm.Sandy))
  //     throw new Error("getAdditionalPoint: sunSail is not a sandy!")
  //   return sunSail.additionalPoints?.[additionalPointIndex]
  // }

  static calcAdditionalPoleIds(ssd: ISail2dForm): PoleId[] {
    const ids: PoleId[] = [];
    for (let i = 0; i < ssd.poles.length; i++) {
      if (ssd.poles[i].additionalPole)
        ids.push(i);
    }
    return ids;
  }

  static calcCornerPoleIds(ssd: ISail2dForm): PoleId[] {
    const ids: PoleId[] = [];
    for (let i = 0; i < ssd.poles.length; i++) {
      if (!ssd.poles[i].additionalPole)
        ids.push(i);
    }
    return ids;
  }

  static calcLastAndNextMainPoles(ssd: ISunSailData | ISail2dSandy, poleId: number): { last: PoleId, next: PoleId } {
    let last = poleId;
    do {
      last = (last + ssd.poles.length - 1) % ssd.poles.length;
    } while (ssd.poles[last].additionalPole)

    let next = poleId;
    do {
      next = (next + 1) % ssd.poles.length;
    } while (ssd.poles[next].additionalPole)
    return { last, next };
  }

  static calcMainPointIds(ssd: ISunSailData | ISail2dForm): PoleId[] {
    const ids: PoleId[] = [];
    const poleCount = Poles.getCount(ssd);
    for (let i = 0; i < poleCount; i++) {
      if (!ssd.poles[i].additionalPole)
        ids.push(i);
    }
    return ids;
  }

  // static #calcHalfVector(sunSail: ISail2dSandy, points: IVec2[], poleId: PoleId): IVec2 {
  //   const { last, next } = SandyLogic.calcLastAndNextMainPoles(sunSail, poleId);
  //   if (sunSail.poles[poleId].additionalPole) {
  //     return Vec2.normalVector(Vec2.dir(points[last], points[next]), true)
  //   } else {
  //     return TriangleHelper.calcHalfVector2(points[poleId], points[last], points[next], { correctDir: "right" })
  //   }
  // }
}